import React, { useState, useEffect, useCallback, useMemo } from "react";
import TableModule from "../TableModule";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCubes, faXmark, faCheck } from "@fortawesome/free-solid-svg-icons";
import apiClient from "../../axios";

const SubscriptionsModule = ({ isVisible, onClose, onAddModules, initialSelectedModules }) => {
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedModules, setSelectedModules] = useState(initialSelectedModules || []);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const itemsPerPage = 10;

    // Procesar los datos obtenidos
    const processFetchedData = (data, initialSelectedModules) => {
        const groupedData = data.reduce((acc, item) => {
            const existing = acc.find((group) => group.moduleId === item.moduleId);
            if (existing) {
                existing.paymentDetails.push({
                    ...item,
                });
            } else {
                acc.push({
                    ...item,
                    paymentDetails: [{ ...item }],
                    selectedPaymentPeriod: item.paymentPeriod,
                    selectedDescription: item.description,
                });
            }
            return acc;
        }, []);

        groupedData.forEach((group) => {
            group.paymentDetails.sort((a, b) => a.paymentPeriod - b.paymentPeriod);
            const firstDetail = group.paymentDetails[0];
            const previouslySelected = initialSelectedModules?.find(
                (mod) => mod.moduleId === group.moduleId
            );
            if (previouslySelected) {
                group.selectedPaymentPeriod = previouslySelected.paymentPeriod;
                group.selectedDescription = previouslySelected.description;
            } else {
                group.selectedPaymentPeriod = firstDetail.paymentPeriod;
                group.selectedDescription = firstDetail.description;
            }
        });

        return groupedData;
    };

    // Obtener datos de la API
    const fetchAllData = useCallback(async () => {
        try {
            setLoading(true);
            /*const response = await apiClient.get(`/module_pack?productType=SUBSCRIPTIONS&page=${page}`);
            if (Array.isArray(response.data.results)) {
                setData(processFetchedData(response.data.results));
                setTotalItems(response.data.total);
            } else {
                setData([]);
                setTotalItems(0);
            }*/
            const response = await apiClient.get(`/module_pack/purchase?productType=SUBSCRIPTIONS`);
            if (Array.isArray(response.data)) {
                setData(processFetchedData(response.data, initialSelectedModules));
                setTotalItems(response.data.length);
            } else {
                setData([]);
                setTotalItems(1);
            }
        } catch (error) {
            console.error("Error al obtener los datos del servicio", error);
        } finally {
            setLoading(false);
        }
    }, [initialSelectedModules]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]); // Añadido 'fetchAllData' a las dependencias

    const handleRefresh = useCallback(() => {
        fetchAllData();
    }, [fetchAllData]);

    // Sincronizar el estado de módulos seleccionados cuando el modal se abre
    /*useEffect(() => {
        if (isVisible) {
            setSelectedModules(initialSelectedModules || []);
        }
    }, [isVisible, initialSelectedModules]);**/
    useEffect(() => {
        if (isVisible) {
            handleRefresh();
            setSelectedModules(initialSelectedModules ? initialSelectedModules.map(mod => mod.moduleCode) : []);
        }
    }, [handleRefresh, isVisible, initialSelectedModules]);
    useEffect(() => {
        if (initialSelectedModules) {
            const codes = initialSelectedModules.map(mod => mod.moduleCode);
            setSelectedModules(codes);
        } else {
            setSelectedModules([]);
        }
    }, [initialSelectedModules]); 

    // Manejar la acción de guardar
    const handleSave = () => {
        const selectedData = data
            .filter((item) => selectedModules.includes(item.moduleCode))
            .map((item) => {
                const selectedDetail = item.paymentDetails.find(
                    (detail) => detail.paymentPeriod === item.selectedPaymentPeriod
                );
                return {
                    ...selectedDetail,
                    count: 0,
                    productType: "SUBSCRIPTIONS" //agregado
                };
            });

        onAddModules(selectedData, "SUBSCRIPTIONS");
        onClose();
    };

    const handleCheckboxChange = (moduleCode) => {
        setSelectedModules((prevSelected) =>
            prevSelected.includes(moduleCode)
                ? prevSelected.filter((code) => code !== moduleCode)
                : [...prevSelected, moduleCode]
        );
    };

    /*const handleSelectAll = () => {
        if (selectedModules.length === data.length) {
            setSelectedModules([]);
        } else {
            setSelectedModules(data.map((item) => item.moduleCode));
        }
    };*/
    const handleSelectAll = () => {
            const pageCodes = currentPageData.map(item => item.moduleCode);
            const allSelected = pageCodes.every(code => selectedModules.includes(code));
    
            if (allSelected) {
                // Deselecciona solo los de esta página
                setSelectedModules(prev =>
                    prev.filter(code => !pageCodes.includes(code))
                );
            } else {
                // Añade los de esta página, sin duplicar
                setSelectedModules(prev => {
                    const newSelection = new Set([...prev, ...pageCodes]);
                    return Array.from(newSelection);
                });
            }
        };
    
        const currentPageData = useMemo(() => {
            const start = (currentPage - 1) * itemsPerPage;
            const end = start + itemsPerPage;
            return data.slice(start, end);
        }, [data, currentPage, itemsPerPage]);
    
        const isAllSelected = currentPageData.every(item => selectedModules.includes(item.moduleCode));

    const handlePaymentPeriodChange = (moduleId, newPeriod) => {
        setData((prevData) =>
            prevData.map((item) => {
                if (item.moduleId === moduleId) {
                    const selectedDetail = item.paymentDetails.find(
                        (detail) => detail.paymentPeriod === newPeriod
                    );
                    return {
                        ...item,
                        selectedPaymentPeriod: newPeriod,
                        selectedDescription: selectedDetail?.description || item.description,
                    };
                }
                return item;
            })
        );
    };

    const renderRow = (item) => {
        const selectedDetail = item.paymentDetails.find(
            (detail) => detail.paymentPeriod === item.selectedPaymentPeriod
        );
        const total = selectedDetail ? selectedDetail.purchasePrice : 0;

        return (
            <>
                <td>{item.moduleName}</td>
                <td>{item.selectedDescription}</td>
                <td>
                    <select
                        value={item.selectedPaymentPeriod}
                        onChange={(e) =>
                            handlePaymentPeriodChange(item.moduleId, Number(e.target.value))
                        }
                    >
                        {item.paymentDetails.map((detail) => (
                            <option key={detail.paymentPeriod} value={detail.paymentPeriod}>
                                {detail.paymentPeriod} meses
                            </option>
                        ))}
                    </select>
                </td>
                <td>${total.toFixed(2)}</td>
                <td className="align-center">
                    <input
                        type="checkbox"
                        checked={selectedModules.includes(item.moduleCode)}
                        onChange={() => handleCheckboxChange(item.moduleCode)}
                    />
                </td>
            </>
        );
    };

    return (
        isVisible && (
            <div className="modal-overlay">
                <div className="modal-content-module">
                    <div className="modal-content-module-body">
                        <h3 className="modal-title ">Suscripciones</h3>
                        <TableModule
                            title="Lista de Suscripciones"
                            rows={data}
                            columns={[
                                { title: "Nombre", key: "description" },
                                { title: "Descripción", key: "description" },
                                { title: "Meses", key: "extraSus" },
                                { title: "Precio Módulo", key: "precioSus" },
                                { title: "Seleccionar", key: "acciones" },
                            ]}
                            icon={faCubes}
                            renderRow={renderRow}
                            currentPage={currentPage}
                            totalItems={totalItems}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                            onRefresh={handleRefresh}
                            onSelectAll={handleSelectAll}
                            isAllSelected={isAllSelected}
                            loading={loading}
                            select={true}
                        />
                    </div>
                    <div className="modulesmodal-basic-form-footer">
                        <button className="basic-custom-button" onClick={onClose}>
                            <FontAwesomeIcon icon={faXmark} className="basic-shortcut-icon" />
                            Cancelar
                        </button>
                        <button className="basic-custom-button" onClick={handleSave}>
                            <FontAwesomeIcon icon={faCheck} className="basic-shortcut-icon" />
                            Aceptar
                        </button>
                    </div>
                </div>
            </div>
        )
    );
};

export default SubscriptionsModule;
